import { marked } from 'marked';

/**
 * Formats raw AI response text into HTML.
 * Ports the vanilla-JS formatAIResponse function from the agent's app.js.
 */
export function formatAIResponse(rawText: string): string {
  const separator = '[X_RDF_CITATION_LIST_X]';
  const refSplit = rawText.split(separator);
  let body = refSplit[0] || rawText;
  const footerText = refSplit[1] || '';

  // Scrub internal metadata tags
  body = body.replace(/\[(Data|SOURCE|PAGE|LINK|FILE):\s*[^\]]+\]/gi, '');
  body = body.replace(/\bPAGE:\s*\d+\b/gi, '');

  // Convert LINK: https://... patterns into markdown links
  body = body.replace(/LINK:\s*(https?:\/\/[^\s)]+)/gi, '[$1]($1)');

  // Convert bare URLs not already in markdown syntax
  body = body.replace(/(?<!\()(?<!\[)(https?:\/\/[^\s)]+)/g, '[$1]($1)');

  // Pre-process color-coded tags
  body = body.replace(
    /\[(Regulatory Body|Regulatory|Body): ([^\]]+)\]/gi,
    '<span class="ai-citation-tag ai-tag-regulatory">$2</span>',
  );
  body = body.replace(
    /\[Action Type: ([^\]]+)\]/gi,
    '<span class="ai-citation-tag ai-tag-action-type">$1</span>',
  );
  body = body.replace(
    /\[Category: ([^\]]+)\]/gi,
    '<span class="ai-citation-tag ai-tag-category">$1</span>',
  );

  // Convert citation numbers to superscripts
  body = body.replace(/\[(\d{1,3})(?:,\s*[^\]]+)?\]/g, (_m, num) => `<sup>${num}</sup>`);
  body = body.replace(/\[\^(\d+)\]/g, '<sup>$1</sup>');

  // Parse markdown
  marked.setOptions({ breaks: true, gfm: true } as any);
  let bodyHtml = marked.parse(body) as string;

  // Ensure all links open in new tab
  bodyHtml = bodyHtml.replace(
    /<a href="(.*?)"/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer"',
  );

  // Build references section — only cited ones
  const citedNumbers = new Set(
    [...bodyHtml.matchAll(/<sup>(\d+)<\/sup>/g)].map(m => m[1]),
  );

  let footerHtml = '';
  if (footerText.trim() && citedNumbers.size === 0) {
    console.warn('formatAIResponse: footer has citations but body cites none — suppressing as likely phantom list');
  }
  if (footerText.trim() && citedNumbers.size > 0) {
    let refItems = '';
    let refCounter = 1;
    footerText.trim().split('\n').forEach(line => {
      if (!line.trim()) return;
      const m = line.match(/^\[?(\d+)[\).\]:]?\s*(.*)/);
      if (!m) return;
      // Skip orphan citation numbers the LLM listed but never cited inline.
      // (Empty citedNumbers is handled above — the whole section is
      // suppressed rather than reaching this loop.)
      if (!citedNumbers.has(m[1])) return;
      let content = m[2].replace(/[|,\s]+$/, '').trim();
      content = content.replace(
        /(https?:\/\/[^\s|]+)/g,
        (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer" class="ai-ref-link">↗ View Source</a>`,
      );
      refItems += `<div class="ai-ref-item"><span class="ai-ref-num">${refCounter++}.</span><span class="ai-ref-text">${content}</span></div>`;
    });
    if (refItems) {
      footerHtml = `<div class="ai-references"><h4>Sources &amp; References</h4>${refItems}</div>`;
    }
  }

  return bodyHtml + footerHtml;
}
